import { useCallback, useEffect, useState, type ChangeEvent, type FocusEvent, type KeyboardEvent } from 'react';
import { Input, Typography } from 'antd';
import SearchBar from '../../../shared/components/SearchBar';
import CustomWarning from '../../../shared/components/CustomWarning';
import type {
  Contact,
  ContactCopiesDeleteTarget,
  ContactDeleteTarget,
  ContactDraft,
  ContactModalState,
} from '../../../types/InfrastructureManagerTypes';
import { useContactGroupOptions, useContacts } from '../../../hooks/useContacts';
import { useInfiniteScrollSentinel } from '../../../hooks/useInfiniteScrollSentinel';
import { ContactGroupPanel } from './components/ContactGroupPanel';
import { ContactGrid } from './components/ContactGrid';
import { ContactFormModal } from './ContactFormModal';
import { CLOSED_CONTACT_MODAL, CLOSED_GROUP_CREATION, SEARCH_DEBOUNCE_MS } from './constants';
import { didSucceed } from './contact.utils';
import styles from './Contacts.module.scss';

const { Title } = Typography;

const Contacts = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const {
    groups,
    isLoadingContacts,
    isContactsLoadingError,
    isFetchingNextPage,
    canLoadMore,
    loadMore,
    isCreatingGroup,
    isSavingContact,
    isDeleting,
    createGroup,
    renameGroup,
    deleteGroup,
    createContact,
    updateContact,
    duplicateContact,
    moveContact,
    deleteContact,
  } = useContacts(debouncedSearch);
  const groupOptions = useContactGroupOptions();
  // Collapsed rather than open groups are tracked, so groups from later pages arrive expanded.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(() => new Set());
  const [groupCreation, setGroupCreation] = useState(CLOSED_GROUP_CREATION);
  const [contactModal, setContactModal] = useState<ContactModalState>(CLOSED_CONTACT_MODAL);
  const [deleteTarget, setDeleteTarget] = useState<ContactDeleteTarget | null>(null);
  const [deleteCopiesTarget, setDeleteCopiesTarget] = useState<ContactCopiesDeleteTarget | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const listSentinelRef = useInfiniteScrollSentinel({ onReachEnd: loadMore, enabled: canLoadMore });

  const toggleGroup = useCallback(
    (groupId: number) =>
      setCollapsedGroups((current) => {
        const next = new Set(current);
        if (!next.delete(groupId)) next.add(groupId);
        return next;
      }),
    [],
  );

  const openContactModal = useCallback((groupId: number) => setContactModal({ ...CLOSED_CONTACT_MODAL, isOpen: true, groupId }), []);

  const openEditContactModal = useCallback((contact: Contact) => setContactModal({ ...CLOSED_CONTACT_MODAL, isOpen: true, contact }), []);

  const handleMoveContact = useCallback(
    (contact: Contact, fromGroupId: number, toGroupId: number) => void didSucceed(moveContact({ contactId: contact.id, fromGroupId, toGroupId })),
    [moveContact],
  );

  const handleDuplicateContact = useCallback(
    (contact: Contact, groupIds: number[]) => void didSucceed(duplicateContact({ contactId: contact.id, groupIds })),
    [duplicateContact],
  );

  const handleDeleteContact = useCallback(
    (contact: Contact, groupId: number, anchor: HTMLElement | null) =>
      setDeleteTarget({ kind: 'contact', id: contact.id, groupId, duplicateCount: contact.duplicateCount, anchor }),
    [],
  );

  const handleDeleteGroup = useCallback((groupId: number, anchor: HTMLElement | null) => setDeleteTarget({ kind: 'group', id: groupId, anchor }), []);

  const handleRenameGroup = useCallback((groupId: number, name: string) => void didSucceed(renameGroup({ groupId, name })), [renameGroup]);

  const startGroupCreation = () => setGroupCreation({ isOpen: true, name: '' });

  const handleNewGroupNameChange = (event: ChangeEvent<HTMLInputElement>) => setGroupCreation({ isOpen: true, name: event.target.value });

  const selectInputText = (event: FocusEvent<HTMLInputElement>) => event.target.select();

  const cancelGroupCreation = () => setGroupCreation(CLOSED_GROUP_CREATION);

  const handleGroupCreationKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') cancelGroupCreation();
  };

  const submitGroupCreation = async () => {
    const name = groupCreation.name.trim();
    if (!name || isCreatingGroup) return;
    if (await didSucceed(createGroup({ name }))) cancelGroupCreation();
  };

  const handleGroupCreationSubmit = () => void submitGroupCreation();

  const closeContactModal = () => setContactModal(CLOSED_CONTACT_MODAL);

  const submitContact = async (fields: ContactDraft) => {
    const { contact, groupId } = contactModal;
    let request: Promise<void> | null = null;
    if (contact) request = updateContact({ contactId: contact.id, fields });
    else if (groupId !== null) request = createContact({ groupId, fields });
    if (request && (await didSucceed(request))) closeContactModal();
  };

  const handleSubmitContact = (fields: ContactDraft) => void submitContact(fields);

  const cancelDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.kind === 'contact' && deleteTarget.duplicateCount > 0) {
      setDeleteCopiesTarget(deleteTarget);
      setDeleteTarget(null);
      return;
    }
    const request =
      deleteTarget.kind === 'group'
        ? deleteGroup({ groupId: deleteTarget.id })
        : deleteContact({ contactId: deleteTarget.id, groupId: deleteTarget.groupId, deleteAll: false });
    if (await didSucceed(request)) setDeleteTarget(null);
  };

  const handleConfirmDelete = () => void confirmDelete();

  // Both answers are deletions with no neutral exit, so the prompt closes whatever the outcome.
  const deleteContactCopies = async (deleteAll: boolean) => {
    if (!deleteCopiesTarget) return;
    await didSucceed(deleteContact({ contactId: deleteCopiesTarget.id, groupId: deleteCopiesTarget.groupId, deleteAll }));
    setDeleteCopiesTarget(null);
  };

  const handleDeleteAllCopies = () => void deleteContactCopies(true);

  const handleDeleteThisCopy = () => void deleteContactCopies(false);

  const showEmptyMessage = !isLoadingContacts && !isContactsLoadingError && groups.length === 0;

  return (
    <main className={styles.layout}>
      <header className={styles.header}>
        <Title level={2} className={styles.title}>
          CONTACT MANAGER
        </Title>
        <div className={styles.toolbar}>
          <SearchBar value={search} onChange={setSearch} className={styles.search} placeholder="SEARCH" />
          <button type="button" className={styles.createButton} onClick={startGroupCreation}>
            <img src="/assets/upload-file-icon.svg" alt="" className={styles.createIcon} />
            CREATE NEW GROUP
          </button>
        </div>
      </header>

      <section className={styles.list} aria-label="Contact groups">
        {groupCreation.isOpen ? (
          <div className={styles.groupCreationBar}>
            <Input
              autoFocus
              className={styles.groupCreationInput}
              value={groupCreation.name}
              onChange={handleNewGroupNameChange}
              onFocus={selectInputText}
              onPressEnter={handleGroupCreationSubmit}
              onBlur={cancelGroupCreation}
              onKeyDown={handleGroupCreationKeyDown}
            />
          </div>
        ) : null}
        {isLoadingContacts ? <p className={styles.message}>Loading contacts...</p> : null}
        {showEmptyMessage ? (
          <p className={styles.message}>{debouncedSearch ? 'No contacts or groups match your search.' : 'No contact groups present.'}</p>
        ) : null}
        {isLoadingContacts
          ? null
          : groups.map((group) => (
              <article key={group.id} className={styles.group}>
                <ContactGroupPanel
                  group={group}
                  isOpen={!collapsedGroups.has(group.id)}
                  onToggle={toggleGroup}
                  onDelete={handleDeleteGroup}
                  onAddContact={openContactModal}
                  onRename={handleRenameGroup}
                />
                {collapsedGroups.has(group.id) ? null : (
                  <div className={styles.tableWrap}>
                    <ContactGrid
                      group={group}
                      groupOptions={groupOptions}
                      onEdit={openEditContactModal}
                      onMove={handleMoveContact}
                      onDuplicate={handleDuplicateContact}
                      onDelete={handleDeleteContact}
                    />
                  </div>
                )}
              </article>
            ))}
        {isFetchingNextPage ? <p className={styles.message}>Loading more groups...</p> : null}
        <div ref={listSentinelRef} className={styles.sentinel} aria-hidden="true" />
      </section>

      <ContactFormModal
        open={contactModal.isOpen}
        contact={contactModal.contact}
        isSaving={isSavingContact}
        onSubmit={handleSubmitContact}
        onClose={closeContactModal}
      />

      <CustomWarning
        open={deleteTarget !== null}
        anchorEl={deleteTarget?.anchor}
        showIcon
        variant="dark"
        message={deleteTarget?.kind === 'group' ? 'Do you want to permanently delete this Group?' : 'Do you want to permanently delete this Contact?'}
        cancelText="NO, GO BACK"
        confirmText="YES, DELETE"
        isLoading={isDeleting}
        onCancel={cancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <CustomWarning
        open={deleteCopiesTarget !== null}
        anchorEl={deleteCopiesTarget?.anchor}
        showIcon
        variant="dark"
        message={`Do you want to delete all copies of this contact in (${deleteCopiesTarget?.duplicateCount ?? 0}) other groups?`}
        cancelText="NO"
        confirmText="YES"
        keyboard={false}
        isLoading={isDeleting}
        onCancel={handleDeleteThisCopy}
        onConfirm={handleDeleteAllCopies}
      />
    </main>
  );
};

export default Contacts;
